import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { user } from 'ampersand-app'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import ProductMainIcon from '@assets/images/product/main_blue_with_icon.svg'
import ProductDocumentIcon from '@assets/images/product/document_blue_with_icon.svg'
import ProductStaffIcon from '@assets/images/product/staff_blue_with_icon.svg'
import CheckIcon from '@assets/images/icons/check.svg'
import { ModuleTypes, ProductTypes, experimentalFeatures } from '@/modules/base/util/constants'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ShoppingCart from '@/modules/moduleSubscription/components/list/shopping-cart/shopping-cart'
import { ButtonIconSquare, CancelButton } from '@/modules/base/components/buttons'
import ModalFooter from '@/modules/base/components/modal/footer/modal-footer'
import ModuleItem from '@/modules/moduleSubscription/components/list/module-item/module-item'
import { Module, ModuleType, Subscription } from '@/modules/moduleSubscription/types/module'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import { addItemToCart } from '@/modules/moduleSubscription/components/_helpers/addItemToCart'
import { removeItemFromCart } from '@/modules/moduleSubscription/components/_helpers/removeItemFromCart'
import SubscriptionService from '@/modules/base/components/subscription/subscriptionService'
import { SubscriptionType, Type } from '@/redux/ducks/subscription/types/details'

interface ModuleSelectionProps {
  subscriptionType?: string;
  selectedSubscription?: Subscription;
  selectedPeriod: string;
  moduleData?: ModuleType;
  onPrevious?: () => void;
  onNext?: (period: string) => void;
  onCancel?: () => void;
  onChangeShoppingCart: (type: Type, module: Module, quantity: number) => void;
  items: ShoppingCartItems;
  enableShoppingCart: boolean;
  enableItemsFromStorage?: boolean;
  enablePeriodSelection?: boolean;
  hideFooter?: boolean
  disableAddingSubscriptionToCart?: boolean
  disableScroll?: boolean
  hideLogo?: boolean
  showAllModules?: boolean
  activeSubscriptions?: SubscriptionType[]
}

const useStyles = makeStyles<ThemeInterface>((theme) => ({
  wrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    height: '100%',
    display: 'flex',
    gap: 30,
    maxWidth: 1274,
  },
  leftContainer: {
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 30,
    [theme.devices.mobile]: {
      gap: 10,
    },
  },
  rightContainer: {
    flex: 1,
    borderLeft: theme.border,
    marginTop: 30,
    paddingLeft: 30,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 20,
  },
  continueButton: {
    marginLeft: 15,
    minWidth: 150,
  },
  cancelButton: {
    minWidth: 150,
  },
  scrollbar: {
    paddingRight: 10,
    '& > .ps__rail-y': {
      zIndex: 1,
    },
    '&.scrollLock': {
      '& > :first-child': {
        height: '100%',
        overflow: 'hidden',
      },
    },
    [theme.devices.mobile]: {
      padding: 0,
      paddingBottom: 140,
    },
  },
  moduleSelection: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: 30,
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-start',
      width: '100%',
    },
  },
  emptySelectionWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.fontSizes.medium,
  },
  allModulesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 35,
    [theme.devices.mobile]: {
      padding: '0px 20px',
      paddingBottom: 160,
    },
  },
}))

const subscriptionTypeIcon = {
  [ProductTypes.MAIN]: ProductMainIcon,
  [ProductTypes.DOCUMENT]: ProductDocumentIcon,
  [ProductTypes.STAFF]: ProductStaffIcon,
}

const getModuleData = async(name: string) => {
  const response = await SubscriptionService.moduleSubscriptions.getModulesOfSubscriptionType(name)
  const data = response?.data
  return { ...data, name }
}

const ModuleSelection: React.FC<ModuleSelectionProps> = (props) => {
  const {
    subscriptionType,
    selectedSubscription,
    selectedPeriod,
    onNext,
    onCancel,
    onChangeShoppingCart,
    moduleData: moduleDataProps,
    items,
    activeSubscriptions,
    enableShoppingCart,
    enableItemsFromStorage = false,
    enablePeriodSelection = false,
    hideFooter = false,
    disableAddingSubscriptionToCart = false,
    disableScroll = false,
    hideLogo = false,
    showAllModules = false,
  } = props
  const classes = useStyles({ showAllModules })

  const [moduleData, setModuleData] = useState<ModuleType | null>(moduleDataProps)
  const [mainModuleData, setMainModuleData] = useState<ModuleType | null>(null)
  const [period, setPeriod] = useState<string>()

  const cachedModuleData = useRef<{ [key: string]: ModuleType }>({})

  useEffect(() => {
    if (showAllModules || !subscriptionType) return
    const hasSubscriptionTypeItems = items?.[subscriptionType] && Object.values(items?.[subscriptionType])?.length

    const activeSub = activeSubscriptions?.find(sub => sub.typeOfSubscription === subscriptionType)
    if (activeSub && (!Object.keys(items).length || !hasSubscriptionTypeItems)) {
      const name = activeSub.type
      if (cachedModuleData.current[name]) {
        setModuleData(cachedModuleData.current[name])
        return
      }

      getModuleData(name).then((moduleData) => {
        setModuleData(moduleData)
        cachedModuleData.current[name] = moduleData
      })
      return
    }

    if (Object.keys(items).length && hasSubscriptionTypeItems && (subscriptionType || moduleData)) {
      const name = Object.values(items[subscriptionType])[0].name
      if (cachedModuleData.current[name]) {
        setModuleData(cachedModuleData.current[name])
        return
      }

      getModuleData(name).then((moduleData) => {
        setModuleData(moduleData)
        cachedModuleData.current[name] = moduleData
      })
    }
  }, [activeSubscriptions, subscriptionType, items])

  useEffect(() => {
    if (!showAllModules) return

    const mainProducts = Object.values(items[ProductTypes.MAIN] ?? {})
    if (!mainProducts.length) {
      setMainModuleData(null)
      return
    }

    const subscriptionProduct = mainProducts.find(sub => sub.type)
    const name = subscriptionProduct
      ? subscriptionProduct.name
      : activeSubscriptions.find(sub => sub.typeOfSubscription === ProductTypes.MAIN)?.type
    if (!name) return

    getModuleData(name).then(setMainModuleData)
  }, [items, activeSubscriptions, subscriptionType])

  useEffect(() => {
    setPeriod(selectedPeriod)
  }, [selectedPeriod])

  useEffect(() => {
    if (selectedSubscription && !disableAddingSubscriptionToCart) {
      const module = {
        ...selectedSubscription,
        bookable: null,
        bookableCount: null,
        owned: null,
        otherSubscription: null,
        singleton: true,
        amount: 1,
        paymentIntervalOfSubscription: period,
        isProductItem: true,
      }

      if (Object.keys(items).length) {
        if (Object.keys(items[subscriptionType]).length) {
          Object.keys(items[subscriptionType]).forEach((moduleName) => {
            removeItemFromCart(subscriptionType, items[subscriptionType][moduleName].name, items, enableItemsFromStorage)
          })
        }
      }
      onAddModuleToCart(subscriptionType, module, 1)
    }
  }, [selectedSubscription, period])

  const onAddModuleToCart = useCallback((type: string, module: Module, value: number) => {
    addItemToCart(type, module, value, true, items, enableItemsFromStorage)
    onChangeShoppingCart?.(type as Type, module, value)
  }, [items])

  const moduleSelection = (moduleData) => {
    if (!moduleData || !moduleData.availableModules?.length) {
      return (
        <div className={clsx(classes.emptySelectionWrapper, classes.moduleSelectionWrapper)}>{lang.upgradeModal.modules.emptySelection}</div>
      )
    }

    const { type: subscriptionType, availableModules = [], notAllowed = false } = moduleData
    return (
      <div className={classes.moduleSelection}>
        {availableModules
          .map(module => {
            let disabled = (items?.[subscriptionType]?.[module.name]?.count >= module.bookable && module.bookable > 0) || notAllowed
            let disabledBy

            if (module.singleton) {
              ({ disabled, disabledBy } = Object.keys(items).reduce((result, sType) => {
                if (sType === subscriptionType) return result
                return Object.keys(items[sType]).reduce((innerResult, subscriptionName) => {
                  const includedModule = items[sType][subscriptionName].modules?.find(({ module: { name } }) => name === module.name)
                  if (!includedModule) return innerResult
                  if (includedModule.includedCount) {
                    return { disabled: true, disabledBy: sType }
                  }
                  return innerResult
                }, result)
              }, { disabled, disabledBy }))
            }

            return (
              <ModuleItem
                key={module.name}
                module={module}
                type={subscriptionType}
                onAddModuleToCart={onAddModuleToCart}
                disabled={disabled}
                disabledBy={disabledBy}
                inCart={Boolean(items?.[subscriptionType]?.[module.name])}
                selectedPeriod={selectedPeriod}
              />
            )
          })}
      </div>
    )
  }

  const allModuleSelections = () => (
    <div className={classes.allModulesContainer}>
      {moduleSelection(mainModuleData)}
    </div>
  )

  const renderShoppingCart = useMemo(() => {
    return (
      <ShoppingCart
        key='shopping-cart-checkout'
        items={items}
        showCheckoutButton={false}
        onChange={onChangeShoppingCart}
        onSelectedPeriodChange={(value) => setPeriod(value)}
        selectedPeriod={period}
        enableItemsFromStorage={enableItemsFromStorage}
        enablePeriodSelection={enablePeriodSelection}
      />
    )
  }, [period, items])

  return (
    <div className={classes.wrapper}>
      <div className={classes.container}>
        <div className={classes.leftContainer}>
          <div className={classes.header}>
            {subscriptionType && !hideLogo && <SVGIcon svg={subscriptionTypeIcon[subscriptionType]} height='30px' width='auto' />}
          </div>
          {showAllModules
            ? allModuleSelections()
            : moduleSelection
              ? <div className={classes.moduleSelectionWrapper}>
                {disableScroll
                  ? moduleSelection(moduleData)
                  : <PerfectScrollbar options={{ suppressScrollX: true }} className={classes.scrollbar}>
                    {moduleSelection(moduleData)}
                  </PerfectScrollbar>}
              </div>
              : <div className={clsx(classes.emptySelectionWrapper, classes.moduleSelectionWrapper)}>{lang.upgradeModal.modules.emptySelection}</div>
          }
        </div>
        {enableShoppingCart && <div className={classes.rightContainer}>{renderShoppingCart}</div>}
      </div>
      {!hideFooter && <ModalFooter>
        <CancelButton
          key='cancel-button'
          customClass={classes.cancelButton}
          label={lang.cancelButtonLabel}
          onClick={onCancel}
        />
        <ButtonIconSquare
          key='next-button'
          customClass={classes.continueButton}
          icon={CheckIcon}
          label={lang.continueButtonLabel}
          onClick={() => onNext(period)}
          disabled={!subscriptionType}
        />
      </ModalFooter>}
    </div>
  )
}

export default ModuleSelection
